/**
 * Internal Conversion Funnel View — /funnel
 *
 * Lightweight, client-side dashboard for inspecting CTA + page-view events
 * captured by `src/lib/tracking.ts`. Reads:
 *   - window.dataLayer (live events this session)
 *   - localStorage("drsully:funnel-events:v1") (persisted across visits)
 *
 * It also subscribes to the "cta:tracked" CustomEvent so new clicks show up
 * without a reload. Purely a UI over local data — no backend writes — so it
 * works as an at-a-glance read of how the in-page funnel is performing in
 * dev / staging / production for whoever has the link.
 *
 * Funnel definition (top → bottom):
 *   page_view  →  cta_click (scan)  →  cta_click (pricing)
 *              →  cta_click (inquire)  →  phone_click
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { SiteNav } from "@/components/site/SiteNav";
import { SiteFooter } from "@/components/site/SiteFooter";
import { PageMeta } from "@/components/site/PageMeta";
import { Button } from "@/components/ui/button";

type Evt = { event: string; ts: number; [k: string]: unknown };

const STORE_KEY = "drsully:funnel-events:v1";
const MAX_EVENTS = 2000;

function loadStored(): Evt[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORE_KEY);
    return raw ? (JSON.parse(raw) as Evt[]) : [];
  } catch {
    return [];
  }
}

function persist(events: Evt[]) {
  try {
    window.localStorage.setItem(STORE_KEY, JSON.stringify(events.slice(-MAX_EVENTS)));
  } catch {
    /* quota — ignore */
  }
}

type Stage = {
  id: string;
  label: string;
  match: (e: Evt) => boolean;
};

const STAGES: Stage[] = [
  { id: "view", label: "Page View", match: (e) => e.event === "page_view" },
  {
    id: "scan",
    label: "Scan CTA Click",
    match: (e) => e.event === "cta_click" && /scan/i.test(String(e.cta_name ?? "")),
  },
  {
    id: "pricing",
    label: "Pricing CTA Click",
    match: (e) => e.event === "cta_click" && String(e.cta_name ?? "") === "pricing_cta",
  },
  {
    id: "inquire",
    label: "Inquire CTA Click",
    match: (e) => e.event === "cta_click" && /inquire/i.test(String(e.cta_name ?? "")),
  },
  {
    id: "phone",
    label: "Phone Click",
    match: (e) => e.event === "cta_click" && String(e.cta_name ?? "") === "phone_click",
  },
];

const Funnel = () => {
  const [events, setEvents] = useState<Evt[]>([]);

  // Merge: persisted history + current dataLayer + subscribe to new fires.
  useEffect(() => {
    const stored = loadStored();
    const live = (window.dataLayer ?? []) as Evt[];
    const merged = dedupe([...stored, ...live]);
    setEvents(merged);
    persist(merged);

    const onTracked = () => {
      const next = dedupe([...(window.dataLayer ?? []) as Evt[]]);
      setEvents((prev) => {
        const m = dedupe([...prev, ...next]);
        persist(m);
        return m;
      });
    };
    window.addEventListener("cta:tracked", onTracked as EventListener);
    return () => window.removeEventListener("cta:tracked", onTracked as EventListener);
  }, []);

  const stageCounts = useMemo(() => {
    return STAGES.map((s) => ({
      ...s,
      count: events.filter(s.match).length,
    }));
  }, [events]);

  const top = stageCounts[0]?.count || 0;
  const ctaBreakdown = useMemo(() => {
    const map = new Map<string, number>();
    events
      .filter((e) => e.event === "cta_click")
      .forEach((e) => {
        const k = String(e.cta_name ?? "unknown");
        map.set(k, (map.get(k) ?? 0) + 1);
      });
    return [...map.entries()].sort((a, b) => b[1] - a[1]);
  }, [events]);

  const reset = () => {
    try {
      window.localStorage.removeItem(STORE_KEY);
    } catch {
      /* ignore */
    }
    setEvents([]);
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <PageMeta
        title="Conversion Funnel — DrSullySpeaks"
        description="Internal funnel view of CTA and page-view events captured client-side."
        path="/funnel"
        noindex
      />
      <SiteNav />

      <section className="pt-32 pb-12 md:pt-40">
        <div className="container max-w-5xl">
          <span className="eyebrow">Internal · Client-side only</span>
          <h1 className="font-display font-bold uppercase text-white text-4xl md:text-6xl mt-5 leading-[1.05]">
            Conversion <span className="text-gradient-gold">Funnel</span>
          </h1>
          <p className="text-silver text-base md:text-lg mt-6 max-w-2xl leading-relaxed">
            Live read of CTA and page-view events captured in this browser.
            Persists locally so you can monitor flow across sessions. Pair with
            GA4 / GTM for the full picture — this is the quick pulse.
          </p>
        </div>
      </section>

      <section className="pb-12">
        <div className="container max-w-5xl">
          <div className="border border-border bg-navy/70 p-6 md:p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display uppercase text-white text-xl tracking-wide">
                Funnel
              </h2>
              <span className="text-[10px] uppercase tracking-[0.25em] text-silver/70">
                {events.length} events captured
              </span>
            </div>

            <ol className="space-y-3">
              {stageCounts.map((s, i) => {
                const pct = top ? Math.round((s.count / top) * 100) : 0;
                const drop =
                  i > 0 && stageCounts[i - 1].count > 0
                    ? Math.round(
                        ((stageCounts[i - 1].count - s.count) / stageCounts[i - 1].count) * 100,
                      )
                    : 0;
                return (
                  <li key={s.id} className="grid grid-cols-12 items-center gap-3">
                    <div className="col-span-12 sm:col-span-3 text-[11px] uppercase tracking-[0.22em] text-silver">
                      {s.label}
                    </div>
                    <div className="col-span-9 sm:col-span-7 relative h-6 bg-navy-elevated/60 border border-border overflow-hidden">
                      <div
                        className="absolute inset-y-0 left-0 bg-gradient-to-r from-orange via-gold to-orange"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <div className="col-span-3 sm:col-span-2 text-right font-display text-white text-sm">
                      {s.count}
                      {i > 0 && s.count > 0 && (
                        <span className="block text-[10px] uppercase tracking-[0.22em] text-silver/60">
                          −{drop}% drop
                        </span>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>
        </div>
      </section>

      <section className="pb-12">
        <div className="container max-w-5xl grid md:grid-cols-2 gap-6">
          <div className="border border-border bg-navy/70 p-6">
            <h3 className="font-display uppercase text-white text-base tracking-wide mb-4">
              CTA Breakdown
            </h3>
            {ctaBreakdown.length === 0 ? (
              <p className="text-silver text-sm">No CTA clicks yet this session.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase tracking-[0.22em] text-silver/70">
                  <tr>
                    <th className="text-left py-2">CTA</th>
                    <th className="text-right py-2">Clicks</th>
                  </tr>
                </thead>
                <tbody>
                  {ctaBreakdown.map(([name, n]) => (
                    <tr key={name} className="border-t border-border">
                      <td className="py-2 text-white">{name}</td>
                      <td className="py-2 text-right text-gold font-display">{n}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="border border-border bg-navy/70 p-6">
            <h3 className="font-display uppercase text-white text-base tracking-wide mb-4">
              Recent Events
            </h3>
            <div className="max-h-72 overflow-auto pr-2 text-xs font-mono space-y-1">
              {events.slice(-30).reverse().map((e, idx) => (
                <div key={idx} className="text-silver/90">
                  <span className="text-gold">{new Date(e.ts).toLocaleTimeString()}</span>{" "}
                  <span className="text-white">{e.event}</span>{" "}
                  {e.cta_name ? <span className="text-silver">· {String(e.cta_name)}</span> : null}
                </div>
              ))}
              {events.length === 0 && (
                <p className="text-silver text-sm font-sans">
                  Nothing captured yet. Browse the site in another tab — events stream in here.
                </p>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="pb-24">
        <div className="container max-w-5xl flex flex-wrap gap-3">
          <Button variant="outline" onClick={reset}>
            Clear stored events
          </Button>
          <Link
            to="/"
            className="inline-flex items-center px-4 py-2 border border-gold/40 text-white text-xs uppercase tracking-[0.2em] hover:bg-gold/10"
          >
            ← Back home
          </Link>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

function dedupe(list: Evt[]): Evt[] {
  // Best-effort dedupe on (event + ts + cta_name) — the dataLayer can contain
  // the same object reference across renders.
  const seen = new Set<string>();
  const out: Evt[] = [];
  for (const e of list) {
    if (!e || typeof e !== "object" || typeof e.ts !== "number") continue;
    const key = `${e.event}|${e.ts}|${e.cta_name ?? ""}|${(e as { cta_path?: string }).cta_path ?? ""}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(e);
  }
  return out.slice(-MAX_EVENTS);
}

export default Funnel;
